/**
 * DEV PLACEHOLDER tileset for 'harbor-edge' (never ships). Drawn into a CanvasTexture under the key
 * the map's tileset declaration expects, only when that texture is missing — so the real
 * `tiles-harbor-edge` image replaces it without code changes. Tile order = HARBOR_TILE ids.
 */
import type Phaser from 'phaser';
import { SeededRng } from '@/core/rng';
import { registerTileset } from '@/maps';
import { HARBOR_TILESET_ID, HARBOR_TILESET_TEXTURE, HARBOR_TILE_COUNT } from './harborEdge';

const T = 32;
type Ctx2D = CanvasRenderingContext2D;

function drawTile(ctx: Ctx2D, id: number, ox: number, rng: SeededRng): void {
  const rect = (x: number, y: number, w: number, h: number, c: string) => { ctx.fillStyle = c; ctx.fillRect(ox + x, y, w, h); };
  switch (id) {
    case 1: // water — teal with two wave dashes
      rect(0, 0, T, T, '#1f8f8a');
      for (let y = 5; y < T; y += 11) { rect(rng.int(1, 8), y, 9, 1, '#6fd3c8'); rect(rng.int(16, 21), y + 5, 8, 1, '#4fbfb2'); }
      break;
    case 2: // ship hull — dark planks with a pale rail line
      rect(0, 0, T, T, '#3a2a1e');
      for (let y = 0; y < T; y += 6) rect(0, y, T, 1, '#241810');
      rect(0, 0, T, 2, '#8a5a32');
      rect(rng.int(4, 24), rng.int(8, 26), 2, 2, '#5a3a1e');
      break;
    case 3: // pier planks
      rect(0, 0, T, T, '#b07a3c');
      for (let y = 0; y < T; y += 8) rect(0, y, T, 1, '#6e4524');
      rect(15, 0, 1, T, '#8a5a32');
      for (let i = 0; i < 3; i++) rect(rng.int(2, 28), rng.int(2, 28), 2, 1, '#c99a5c');
      break;
    case 4: // gangway foot — planks with cleats
      rect(0, 0, T, T, '#b07a3c');
      for (let y = 0; y < T; y += 8) rect(0, y, T, 1, '#6e4524');
      rect(4, 3, 24, 2, '#3a2a1e'); rect(4, 14, 24, 2, '#3a2a1e'); rect(4, 25, 24, 2, '#3a2a1e');
      break;
    case 5: // sand
      rect(0, 0, T, T, '#e2c48c');
      for (let i = 0; i < 9; i++) rect(rng.int(1, T - 2), rng.int(1, T - 2), 1, 1, '#c9a86e');
      break;
    case 6: // crate on sand
      rect(0, 0, T, T, '#e2c48c');
      rect(3, 5, 26, 24, '#a8743a');
      rect(3, 5, 26, 1, '#d9a45a'); rect(3, 28, 26, 1, '#5a3a1e'); rect(3, 5, 1, 24, '#d9a45a'); rect(28, 5, 1, 24, '#5a3a1e');
      for (let i = 0; i < 22; i++) { rect(5 + i, 7 + i, 1, 1, '#5a3a1e'); rect(26 - i, 7 + i, 1, 1, '#5a3a1e'); }
      break;
    case 7: // palm trunk on sand
      rect(0, 0, T, T, '#e2c48c');
      rect(12, 0, 8, 32, '#5a3a1e'); rect(14, 0, 2, 32, '#8a5a32');
      for (let y = 4; y < 32; y += 6) rect(12, y, 8, 1, '#3a2412');
      break;
    default: // 0 empty — transparent
      break;
  }
}

/** Registers the tileset and draws the placeholder texture if the real one is missing. Idempotent. */
export function ensureHarborDevTileset(scene: Phaser.Scene): string {
  registerTileset({ id: HARBOR_TILESET_ID, texture: HARBOR_TILESET_TEXTURE, tileWidth: T, tileHeight: T });
  if (scene.textures.exists(HARBOR_TILESET_TEXTURE)) return HARBOR_TILESET_TEXTURE;
  const tex = scene.textures.createCanvas(HARBOR_TILESET_TEXTURE, HARBOR_TILE_COUNT * T, T);
  if (!tex) { console.warn('[prologue] could not create the dev harbor tileset'); return HARBOR_TILESET_TEXTURE; }
  const ctx = tex.context;
  const rng = new SeededRng('dev-harbor-tiles');
  ctx.clearRect(0, 0, HARBOR_TILE_COUNT * T, T);
  for (let i = 0; i < HARBOR_TILE_COUNT; i++) drawTile(ctx, i, i * T, rng);
  tex.refresh();
  return HARBOR_TILESET_TEXTURE;
}
